/**
 * Investigation results for the EPIC scenarios — physical exam, labs, and
 * imaging the student can "order". When a scenario is active the instructor
 * reveals these via sub-buttons; the result card is pushed to the learner
 * monitor.
 *
 * Findings are synthesised to be consistent with each scenario's monitor
 * physiology and the diagnostic guidance in the Stanford Emergency Manual.
 * They are teaching values, NOT prescriptive — adapt to your case.
 *
 * Shape: { [scenarioKey]: [ { title, lines: string[] } ] }
 * `title` should name the investigation (e.g. "Physical Exam", "ABG", "POCUS").
 */
export const EPIC_INVESTIGATIONS = {
  epicAsystolePEA: [
    { title: 'Physical Exam', lines: [
      'No palpable central pulse; no heart sounds',
      'Apnoeic; no spontaneous movement',
      'Pupils dilating, sluggish/absent reflexes',
      'Skin mottled, cool, peripherally cyanosed',
    ] },
    { title: 'ABG / Labs', lines: [
      'pH 6.92 · PaCO₂ 68 · PaO₂ 55 · HCO₃ 12 (mixed acidosis)',
      'Lactate 11 mmol/L; base deficit −18',
      'K⁺ 6.8 mmol/L (if hyperkalaemic arrest)',
      'iCa 0.8, glucose 60 — check reversible causes',
    ] },
    { title: 'POCUS (during pulse check)', lines: [
      'Cardiac standstill vs. organised wall motion (PEA)',
      'Assess for pericardial effusion / tamponade',
      'IVC, lung sliding (rule out tension pneumothorax)',
      'RV dilation → suspect massive PE',
    ] },
  ],

  epicBradycardia: [
    { title: 'Physical Exam', lines: [
      'Slow, may be weak/thready pulse; poor perfusion',
      'Pallor, diaphoresis, altered mental status if severe',
      'Look for surgical vagal stimulus (peritoneum, eyes)',
    ] },
    { title: '12-Lead ECG', lines: [
      'Sinus bradycardia 38 bpm, or junctional escape',
      'Assess for AV block (1°/2°/3°) and P–QRS relationship',
      'Check for ischaemic ST/T changes (esp. inferior/RCA)',
    ] },
    { title: 'ABG / Labs', lines: [
      'K⁺ 6.2 mmol/L if hyperkalaemic; check Mg, iCa',
      'ABG for hypoxaemia / acidosis as a driver',
      'Troponin and BNP if ischaemia suspected',
    ] },
  ],

  epicSVT: [
    { title: '12-Lead ECG', lines: [
      'Narrow-complex regular tachycardia ~190, no visible P waves',
      'If wide + regular: assume VT until proven otherwise',
      'If irregularly irregular: AFib with RVR',
      'Look for delta wave (WPW) — avoid AV nodal blockers',
    ] },
    { title: 'Physical Exam', lines: [
      'Rapid thready pulse; hypotensive, cool peripheries',
      'Chest pain, dyspnoea, or altered mental status = unstable',
    ] },
  ],

  epicVFVT: [
    { title: '12-Lead / Rhythm', lines: [
      'Coarse ventricular fibrillation (shockable)',
      'Or monomorphic/polymorphic VT with no pulse',
      'Recheck rhythm every 2 min at compressor change',
    ] },
    { title: 'ABG / Labs', lines: [
      'Severe mixed acidosis; lactate markedly elevated',
      'K⁺ (hyper- or hypo-), Mg — treat reversible causes',
      'iCa, glucose; troponin post-ROSC',
    ] },
  ],

  epicAnaphylaxis: [
    { title: 'Physical Exam', lines: [
      'Urticaria, flushing, angioedema (lips/tongue/face)',
      'Diffuse wheeze; high inspiratory pressures',
      'Weak, rapid pulse; profound hypotension',
      '(Skin signs may be ABSENT under drapes)',
    ] },
    { title: 'Serum Tryptase', lines: [
      'Peak tryptase 45 ng/mL (normal < 11.4)',
      'Draw 1–2 hr after onset; repeat baseline at 24 hr',
      'Supports mast-cell degranulation diagnosis',
    ] },
    { title: 'ABG / POCUS', lines: [
      'ABG: hypoxaemia + respiratory acidosis (air-trapping)',
      'POCUS: hyperdynamic, under-filled ("empty") LV',
      'Distributive shock — low SVR physiology',
    ] },
  ],

  epicBronchospasm: [
    { title: 'Physical Exam', lines: [
      'Diffuse expiratory wheeze; prolonged expiration',
      'Silent chest if severe (ominous)',
      'High peak pressures, low returned tidal volume',
    ] },
    { title: 'Capnography', lines: [
      'Upsloping ("shark-fin") EtCO₂ waveform',
      'Rising EtCO₂ 60 mmHg; prolonged expiratory phase',
    ] },
    { title: 'ABG', lines: [
      'pH 7.28 · PaCO₂ 58 · PaO₂ 62 (respiratory acidosis, hypoxaemia)',
      'Consider tryptase if anaphylaxis in differential',
    ] },
  ],

  epicDelayedEmergence: [
    { title: 'Neuro Exam', lines: [
      'GCS reduced; minimal response to voice/pain',
      'Pupils: pinpoint → opioid; check symmetry (stroke)',
      'Motor asymmetry or absent gag → urgent imaging',
    ] },
    { title: 'TOF / Depth', lines: [
      'TOF: fade / count < 4 → residual paralysis (reverse)',
      'Confirm all volatile and IV agents are off',
    ] },
    { title: 'ABG / Labs', lines: [
      'ABG: PaCO₂ 60 (hypercarbia/narcosis)',
      'Glucose 55 (hypoglycaemia); Na 118 or 158',
      'iCa; consider drug levels / med-error review',
    ] },
    { title: 'Imaging', lines: [
      'STAT non-contrast head CT if focal deficit/stroke suspected',
      'Neurology / Neurosurgery consult',
    ] },
  ],

  epicDifficultAirway: [
    { title: 'Airway Exam', lines: [
      'Cannot intubate; poor/absent view at laryngoscopy',
      'Cannot mask ventilate; falling SpO₂ 68%',
      'Absent/inadequate EtCO₂ trace',
    ] },
    { title: 'Monitors', lines: [
      'SpO₂ falling despite 100% O₂',
      'Capnography flat = no ventilation → proceed to eFONA',
    ] },
  ],

  epicHypoxemia: [
    { title: 'Physical Exam', lines: [
      'Central cyanosis; assess bilateral breath sounds',
      'Asymmetry → endobronchial intubation or pneumothorax',
      'Crackles → pulmonary oedema; wheeze → bronchospasm',
    ] },
    { title: 'ABG / Co-oximetry', lines: [
      'PaO₂ 55 on high FiO₂ (shunt physiology)',
      'Confirm SpO₂ with ABG; send co-oximetry',
      'MetHb ~85% SpO₂ plateau; COHb — SpO₂ falsely normal',
    ] },
    { title: 'Lung US / CXR', lines: [
      'US: B-lines (oedema), absent sliding (PTX), consolidation',
      'STAT portable CXR; fibreoptic to confirm ETT position',
    ] },
  ],

  epicHighAirwayPressure: [
    { title: 'Physical Exam', lines: [
      'Peak pressure > 35 cmH₂O or > 5 above baseline',
      'Auscultate: asymmetry (mainstem/PTX) vs wheeze vs crackles',
      'Reduced returned tidal volume',
    ] },
    { title: 'Bedside Tests', lines: [
      'Pass suction catheter — kink / mucous plug',
      'Disconnect circuit — resolves if auto-PEEP (air-trapping)',
      'Squeeze bag: high = machine/circuit; low = ETT/lungs',
    ] },
  ],

  epicHypotension: [
    { title: 'Physical Exam', lines: [
      'Weak, thready pulse; check for concealed haemorrhage',
      'Warm & vasodilated (distributive) vs cool (cardiogenic/hypovolaemic)',
      'Inspect surgical field for vessel/heart compression',
    ] },
    { title: 'POCUS / TEE', lines: [
      'Volume status (IVC, LV filling)',
      'LV/RV function; pericardial effusion; valvular disease',
      'B-lines / lung sliding',
    ] },
    { title: 'ABG / Labs', lines: [
      'Lactate 4.5, base deficit −8 (hypoperfusion)',
      'Hgb 7 if haemorrhage; iCa, K⁺, glucose',
    ] },
  ],

  epicHypertension: [
    { title: 'Confirm & Exam', lines: [
      'Verify a-line transducer height / zero; recheck NIBP cuff',
      'Assess depth of anaesthesia and analgesia',
      'Check for full bladder, tourniquet, hypercarbia',
    ] },
    { title: '12-Lead ECG / Labs', lines: [
      'ECG for ischaemia (ST change, T inversion, arrhythmia)',
      'ABG (hypercarbia); consider troponin',
      'If episodic/refractory: consider phaeochromocytoma workup',
    ] },
  ],

  epicMyocardialIschemia: [
    { title: '12-Lead ECG', lines: [
      'ST elevation in contiguous leads (STEMI) — expand to II, V5',
      'Or ST depression / T-wave inversion (ischaemia)',
      'New conduction abnormality (e.g. LBBB) or arrhythmia',
    ] },
    { title: 'Labs', lines: [
      'Troponin rising / elevated; send serial troponins',
      'ABG, electrolytes, Hgb, coagulation panel',
    ] },
    { title: 'TEE / TTE', lines: [
      'New regional wall-motion abnormality',
      'New/worsened mitral regurgitation',
      'Assess LV function to guide inotrope choice',
    ] },
  ],

  epicHemorrhage: [
    { title: 'Physical Exam', lines: [
      'Pallor, tachycardia 142, hypotension 72/42, cool peripheries',
      'Rising suction canister volume; soaked sponges',
      'Surgical field: identify bleeding source',
    ] },
    { title: 'Labs / TEG', lines: [
      'Hgb 6.0 g/dL; platelets 78 K; INR 1.8; fibrinogen 90 mg/dL',
      'Lactate 5.5, base deficit −9; iCa 0.9 (transfusion)',
      'TEG/ROTEM: prolonged R, low MA, low α-angle',
    ] },
    { title: 'FAST / POCUS', lines: [
      'Free fluid in abdomen/pelvis (positive FAST)',
      'Under-filled IVC and hyperdynamic LV',
    ] },
  ],

  epicPulmonaryEmbolism: [
    { title: 'Physical Exam', lines: [
      'Sudden EtCO₂ drop 18; hypoxaemia, hypotension',
      'Raised CVP / distended neck veins',
      'Awake: dyspnoea, chest pain, cough',
    ] },
    { title: '12-Lead ECG', lines: [
      'Sinus tachycardia; S1Q3T3 pattern',
      'New RBBB; right-axis deviation; T inversion V1–V4 (RV strain)',
    ] },
    { title: 'TEE / TTE', lines: [
      'Dilated, hypokinetic RV; D-shaped septum',
      'McConnell sign (apical sparing); ± visible thrombus/air',
      'Elevated RVSP; under-filled LV',
    ] },
  ],

  epicRightHeartFailure: [
    { title: 'TEE / TTE', lines: [
      'TAPSE severely reduced < 6 mm (normal 16–20)',
      'Dilated / hypertrophied RV; flattened septum (D-shaped LV)',
      'Under-filled LV despite adequate preload',
    ] },
    { title: 'Labs / Exam', lines: [
      'CVP 20 mmHg, SvO₂ < 65% (RV overload)',
      'BNP elevated; lactate rising',
      'JVD, peripheral oedema, hepatic congestion',
    ] },
  ],

  epicHighSpinal: [
    { title: 'Neuro / Motor Exam', lines: [
      'Sensory level ascending; upper-limb numbness',
      'Weak hand grip; diaphragmatic weakness → apnoea',
      'Difficulty swallowing/phonating; may lose consciousness',
    ] },
    { title: 'Haemodynamics', lines: [
      'Bradycardia 38 + profound hypotension 48/28 (sympathectomy)',
      'If LAST possible: give 20% lipid emulsion',
    ] },
  ],

  epicTransfusionReaction: [
    { title: 'Physical Exam', lines: [
      'Fever 39.2 °C, rigors; flank/back pain',
      'Hypotension, tachycardia; dark (haemoglobinuric) urine',
      'Urticaria/wheeze if anaphylactic; oozing if DIC',
    ] },
    { title: 'Haemolysis Labs', lines: [
      'LDH ↑, haptoglobin ↓, indirect bilirubin ↑',
      'Plasma/urine free haemoglobin positive',
      'Direct antiglobulin test (DAT/Coombs) positive',
      'DIC: platelets ↓, fibrinogen ↓, D-dimer ↑',
    ] },
    { title: 'Blood Bank', lines: [
      'Clerical check: verify patient–unit identity',
      'Return units + post-transfusion sample per protocol',
      'Rule out bacterial contamination (gram stain/culture)',
    ] },
  ],

  epicTrauma: [
    { title: 'Primary Survey', lines: [
      'A: airway patency, c-spine; B: bilateral breath sounds, chest wall',
      'C: external haemorrhage, pulses, capillary refill',
      'D: GCS, pupils; E: full exposure, log-roll, temperature',
    ] },
    { title: 'FAST / Imaging', lines: [
      'FAST: free fluid (peritoneal/pericardial/pleural)',
      'eFAST: pneumothorax (absent sliding)',
      'Portable CXR/pelvis; CT when stabilised',
    ] },
    { title: 'Labs', lines: [
      'Hgb 8, lactate 6, base deficit −10',
      'INR/fibrinogen (trauma coagulopathy); iCa; type & cross',
    ] },
  ],

  epicMalignantHyperthermia: [
    { title: 'Physical Exam', lines: [
      'Masseter spasm / generalised rigidity (no shivering)',
      'Rising core temp 40 °C; skin hot, mottled, diaphoretic',
      'Tachycardia, tachypnoea; ↑↑ EtCO₂ despite ventilation',
    ] },
    { title: 'ABG / Chemistry', lines: [
      'pH 7.10 · PaCO₂ 70 · HCO₃ 15 (mixed acidosis)',
      'K⁺ 6.8 mmol/L; iCa low',
      'CK markedly elevated; myoglobinuria (dark urine)',
      'Lactate high',
    ] },
    { title: 'Capnography', lines: [
      'Rapidly rising EtCO₂ 65+ unresponsive to ↑ minute ventilation',
      'Earliest and most sensitive sign',
    ] },
  ],

  epicLAST: [
    { title: 'Physical Exam', lines: [
      'Neuro: perioral numbness, tinnitus, metallic taste → seizure/AMS',
      'Cardiac: hypotension, brady/tachyarrhythmia, collapse',
      'Temporal link to recent local anaesthetic dose',
    ] },
    { title: '12-Lead ECG', lines: [
      'Wide QRS; PR/QT prolongation',
      'Ventricular ectopy → VT / VF; bradycardia; asystole',
    ] },
  ],

  epicPneumothorax: [
    { title: 'Physical Exam', lines: [
      'Decreased/absent breath sounds on affected side',
      'Hyperresonance to percussion; ↑ peak pressures',
      'Distended neck veins, ↑ CVP; tracheal deviation (late)',
      'Hypotension 84/54, tachycardia, hypoxaemia',
    ] },
    { title: 'Lung Ultrasound', lines: [
      'Absent lung sliding; lung point (specific for PTX)',
      'M-mode: "barcode/stratosphere" sign (vs seashore)',
      'Absent B-lines / comet tails on affected side',
    ] },
    { title: 'CXR', lines: [
      'Visible pleural line, absent lung markings peripherally',
      'Mediastinal shift if tension — do NOT delay decompression',
    ] },
  ],

  epicOxygenFailure: [
    { title: 'Gas Analyser', lines: [
      'FiO₂ reads inappropriately low despite "100% O₂"',
      'O₂ pipeline pressure low; flow meter reads low',
      'Confirms supply failure / crossover → ventilate off-machine',
    ] },
    { title: 'Exam', lines: [
      'Falling SpO₂ 85%; verify chest rise with Ambu on cylinder',
      'Capnography confirms ventilation with backup source',
    ] },
  ],

  epicPowerFailure: [
    { title: 'Situation Check', lines: [
      'Sudden loss of lights and mains-powered equipment',
      'Ventilator on battery (~30 min); monitors may be down',
      'Confirm pulse manually; manual BP; transport monitor',
      'Verify emergency (red) outlets are live',
    ] },
  ],

  epicAirwayFire: [
    { title: 'Airway Exam (after control)', lines: [
      'Soot/carbon in airway; singed mucosa; oedema',
      'Inspect removed ETT for missing fragments',
      'Bronchoscopy: extent of thermal injury, residual debris',
    ] },
    { title: 'Monitoring', lines: [
      'Watch for progressive airway oedema → early re-intubation',
      'SpO₂ / capnography after re-establishing ventilation',
    ] },
  ],

  epicORFire: [
    { title: 'Patient Assessment', lines: [
      'Survey skin/drapes for burns once flames out',
      'Assess airway involvement (→ Fire — Airway)',
      'Monitor vital signs; ensure adequate anaesthesia',
    ] },
  ],
};
